import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTheme } from "@glossy";
import { form } from "@glossy";
import { tr } from "@linkfluence/i18n";
import { User } from "@linkfluence/models/user";
import Button from "@ui-kit/button";
import Form, { Fieldset, Footer, Label, Text } from "@ui-kit/form";
import Portrait from "@ui-kit/identity/portrait";
import { Error } from "../shared";

interface IProps {
  user: User;
  onUpdateUser: (data: Partial<User>) => void;
  onOpenAvatarSelections: () => void;
  picture: string;
  onCancel: () => void;
}

type Inputs = {
  name: string;
  email: string;
};

const usePersoForm = (name, email, onUpdateUser, onCancel) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<Inputs>({
    mode: "onChange",
    defaultValues: { name, email },
  });

  const handlers = useMemo(
    () => ({
      onSave: (data: Inputs) => {
        onUpdateUser(data);
      },
      onReset: (event) => {
        reset({ name, email });
        event.preventDefault();
        event.stopPropagation();
        onCancel();
      },
    }),
    [email, name, onCancel, onUpdateUser, reset]
  );

  return [control, errors, handleSubmit, isValid, handlers] as const;
};

const UiKitProfilePerso = ({
  user: { name, email, locale },
  onUpdateUser,
  onOpenAvatarSelections,
  picture,
  onCancel,
}: IProps) => {
  const theme = useTheme();

  const [control, errors, handleSubmit, isValid, { onSave, onReset }] =
    usePersoForm(name, email, onUpdateUser, onCancel);

  return (
    <Form>
      <Fieldset>
        <Label htmlFor="name">{tr(locale, "PROFILE::NAME::TITLE")}</Label>
        <Controller
          name="name"
          rules={{ required: true }}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Text
              variant="primary"
              onChange={onChange}
              value={value}
              placeholder={tr(locale, "PROFILE::NAME::PLACEHOLDER")}
              $size="l"
            />
          )}
        />
        {errors.name && (
          <Error>{tr(locale, "PROFILE::NAME::PLACEHOLDER")}</Error>
        )}
      </Fieldset>
      <Fieldset>
        <Label htmlFor="email">{tr(locale, "PROFILE::EMAIL::TITLE")}</Label>
        <Controller
          name="email"
          rules={{
            required: true,
            pattern: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/,
          }}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Text
              variant="primary"
              type="email"
              onChange={onChange}
              value={value}
              placeholder={tr(locale, "PROFILE::EMAIL::PLACEHOLDER")}
              $size="l"
              disabled
            />
          )}
        />
        {errors.email && (
          <Error>{tr(locale, "PROFILE::EMAIL::PLACEHOLDER")}</Error>
        )}
      </Fieldset>
      <Fieldset>
        <Label>{tr(locale, "PROFILE::AVATAR::TITLE")}</Label>
        <Portrait
          picture={picture}
          editable
          borderColor={form(theme, "primary")}
          onClick={onOpenAvatarSelections}
          size="l"
        />
      </Fieldset>
      <Footer>
        <Button
          onClick={onReset}
          text={tr(locale, "_::CANCEL")}
          variant="tertiary"
        />
        <Button
          isDisabled={!isValid}
          onClick={handleSubmit(onSave)}
          text={tr(locale, "_::SAVE")}
          variant="primary"
        />
      </Footer>
    </Form>
  );
};

export default UiKitProfilePerso;
